import type { ReactNode } from 'react';
import { CircleAlert, Inbox, LoaderCircle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Botao } from './base';
import { ErroRequisicao, mensagemDeErro } from '../nucleo/api';
import { iniciarAjusteDeFoco } from '../nucleo/foco';

export function Carregando({ texto }: { texto?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" className="flex min-h-40 items-center justify-center gap-2 text-sm text-tinta-3">
      <LoaderCircle className="size-4 animate-spin" aria-hidden />
      {texto ?? t('comum.carregando')}
    </div>
  );
}

/** `idTitulo` e `idTexto` (opcionais, sem efeito visual) deixam um contêiner usar o título como nome e o texto como descrição. */
export function Vazio({ titulo, texto, acao, icone, idTitulo, idTexto }: { titulo?: string; texto?: string; acao?: ReactNode; icone?: ReactNode; idTitulo?: string; idTexto?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <div className="mb-1 rounded-full border border-borda p-3 text-tinta-3">{icone ?? <Inbox className="size-5" aria-hidden />}</div>
      <p id={idTitulo} className="font-medium text-tinta-1">
        {titulo ?? t('estados.vazioTitulo')}
      </p>
      <p id={idTexto} className="max-w-sm text-sm text-tinta-3">
        {texto ?? t('estados.vazioTexto')}
      </p>
      {acao && <div className="mt-3">{acao}</div>}
    </div>
  );
}

const SELETOR_TITULO = 'h1, h2, h3, [role="heading"]';
const ESPERA_RECARGA_MS = 10_000;

/**
 * r8 visual B2: depois de "Tentar de novo" o estado de erro some e o foco caía no body. Aqui o foco vai ao título do
 * conteúdo recarregado (ou ao próprio conteúdo) dentro do mesmo contêiner, assim que o erro sair da tela e não houver mais
 * carregamento. Se o usuário já moveu o foco, ou o erro voltou (o botão continua na tela), nada muda.
 */
export function focarConteudoRecarregado(botao: HTMLElement) {
  const alerta = botao.closest('[role="alert"]');
  const conteiner = alerta?.parentElement;
  if (!alerta || !conteiner) return;
  const inicio = performance.now();
  let semNada = 0;
  // Review r33 (robustez B2): o vigia global espera este ajuste (até 10 s) em vez de mandar o foco ao conteúdo antes.
  const fim = iniciarAjusteDeFoco();
  const focar = (el: HTMLElement) => {
    fim();
    if (el.tabIndex < 0 && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: false });
  };
  const passo = () => {
    const ativo = document.activeElement;
    const focoPerdido = !ativo || ativo === document.body || !ativo.isConnected;
    if (botao.isConnected) {
      // O erro ainda está na tela (ou voltou): o foco fica no botão.
      if (performance.now() - inicio < ESPERA_RECARGA_MS) requestAnimationFrame(passo);
      else fim();
      return;
    }
    if (!focoPerdido || !conteiner.isConnected) return fim();
    const carregando = conteiner.querySelector('[role="status"][aria-live]');
    const titulo = conteiner.querySelector<HTMLElement>(SELETOR_TITULO);
    if (titulo && !carregando) return focar(titulo);
    // Sem título e sem carregamento por alguns quadros: o conteúdo recarregado é o próprio contêiner.
    if (!carregando) semNada++;
    if (semNada < 4 && performance.now() - inicio < ESPERA_RECARGA_MS) return void requestAnimationFrame(passo);
    focar(titulo ?? conteiner);
  };
  requestAnimationFrame(passo);
}

export function Erro({ erro, aoTentar }: { erro?: unknown; aoTentar?: () => void }) {
  const { t } = useTranslation();
  // Review r7 robustez (baixo 7): registro inexistente (404) não volta com nova tentativa; o botão só atrapalha.
  if (erro instanceof ErroRequisicao && erro.status === 404) aoTentar = undefined;
  return (
    <div role="alert" className="flex min-h-48 flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <div className="mb-1 rounded-full border border-perigo p-3 text-perigo">
        <CircleAlert className="size-5" aria-hidden />
      </div>
      <p className="font-medium text-tinta-1">{t('estados.erroTitulo')}</p>
      <p className="max-w-sm text-sm text-tinta-3">{erro ? mensagemDeErro(erro) : t('estados.erroTexto')}</p>
      <p className="max-w-sm text-sm text-tinta-3">{t('estados.erroContinua')}</p>
      {aoTentar && (
        <Botao
          className="mt-3"
          onClick={(e) => {
            const botao = e.currentTarget;
            aoTentar();
            focarConteudoRecarregado(botao);
          }}
        >
          {t('estados.tentarDeNovo')}
        </Botao>
      )}
    </div>
  );
}

export function SemPermissao() {
  const { t } = useTranslation();
  return <Vazio icone={<Lock className="size-5" aria-hidden />} titulo={t('estados.semPermissao')} texto="" />;
}

/** Envelope padrão para dados de useQuery: carregando, erro, vazio e conteúdo. */
export function Estado<T>({
  consulta,
  vazio,
  children,
}: {
  consulta: { isPending: boolean; isError: boolean; error: unknown; data: T | undefined; refetch: () => unknown };
  vazio?: (d: T) => boolean;
  children: (d: T) => ReactNode;
}) {
  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <Erro erro={consulta.error} aoTentar={() => consulta.refetch()} />;
  const d = consulta.data as T;
  if (vazio?.(d)) return <Vazio />;
  return <>{children(d)}</>;
}
